import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip);

interface SLAComplianceGaugeProps {
  slaCompliance: number;  // Percentage 0-100
  slaMet: number;
  slaBreached: number;
  slaBreachedPercent?: number;  // Percentage 0-100
  totalSlaApplied?: number;
  onSlaClick?: (slaBreached: boolean) => void;
}

const SLAComplianceGauge: React.FC<SLAComplianceGaugeProps> = ({
  slaCompliance,
  slaMet,
  slaBreached,
  slaBreachedPercent,
  totalSlaApplied,
  onSlaClick
}) => {
  // Calculate breach percent if not provided
  const breachPercent = slaBreachedPercent ?? (totalSlaApplied && totalSlaApplied > 0
    ? Math.round((slaBreached / totalSlaApplied) * 100 * 10) / 10
    : 0);

  // Chart shows breach percentage (red portion = breached)
  const chartData = {
    datasets: [
      {
        data: [breachPercent, 100 - breachPercent],
        backgroundColor: ['rgba(239, 68, 68, 0.9)', 'rgba(16, 185, 129, 0.9)'],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    }
  };

  const statusLabel = breachPercent <= 10 ? 'Excellent' :
    breachPercent <= 30 ? 'Fair' : 'Needs Attention';

  const statusColor = breachPercent <= 10 ? 'text-green-600' :
    breachPercent <= 30 ? 'text-yellow-600' : 'text-red-600';

  const total = totalSlaApplied ?? (slaMet + slaBreached);

  return (
    <div id="chart-sla-gauge" className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-2">SLA Performance</h3>
      <div className="relative h-32">
        <Doughnut data={chartData} options={options} />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
          <span className="text-3xl font-bold text-red-600">{breachPercent.toFixed(1)}%</span>
          <span className="text-xs text-gray-500">Breached</span>
          <span className={`text-sm font-medium ${statusColor}`}>{statusLabel}</span>
        </div>
      </div>

      {/* Total SLA Applied indicator */}
      <div className="mt-2 text-center">
        <span className="text-sm text-gray-500">
          Out of <span className="font-semibold text-gray-700">{total}</span> SLAs Applied
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4 text-center">
        <div
          className={`bg-green-50 rounded-lg p-3 ${onSlaClick ? 'cursor-pointer hover:bg-green-100 transition-colors' : ''}`}
          onClick={() => onSlaClick && onSlaClick(false)}
        >
          <div className="text-2xl font-bold text-green-700">{slaMet}</div>
          <div className="text-xs text-green-600">SLA Met ({slaCompliance.toFixed(1)}%)</div>
        </div>
        <div
          className={`bg-red-50 rounded-lg p-3 ${onSlaClick ? 'cursor-pointer hover:bg-red-100 transition-colors' : ''}`}
          onClick={() => onSlaClick && onSlaClick(true)}
        >
          <div className="text-2xl font-bold text-red-700">{slaBreached}</div>
          <div className="text-xs text-red-600">SLA Breached ({breachPercent.toFixed(1)}%)</div>
        </div>
      </div>
    </div>
  );
};

export default SLAComplianceGauge;
